import { GroupedReaction, ReactorInfo, TicketCommentReaction } from '@/types/TicketCommentReaction'

/**
 * Maps emoji identifiers to their display characters.
 * Airbnb uses text identifiers; we need to map them to emoji characters.
 */
export const EMOJI_MAP: Record<string, string> = {
  thumbs_up: '\uD83D\uDC4D',
  thumbs_down: '\uD83D\uDC4E',
  heart: '\u2764\uFE0F',
  clap: '\uD83D\uDC4F',
  smile: '\uD83D\uDE0A',
  fire: '\uD83D\uDD25',
  check: '\u2705',
  eyes: '\uD83D\uDC40',
  rocket: '\uD83D\uDE80',
  party: '\uD83C\uDF89',
}

/**
 * Converts emoji identifier to display character.
 * Falls back to identifier if not found in map.
 */
export function getEmojiDisplay(emoji: string): string {
  return EMOJI_MAP[emoji] || emoji
}

/**
 * Groups reactions by emoji for display.
 * @param reactions - Array of reactions from GraphQL
 * @param currentUserId - Current logged-in user ID (for hasCurrentUserReacted)
 * @returns Array of grouped reactions with counts and reactor info
 */
export function groupReactionsByEmoji(reactions: TicketCommentReaction[], currentUserId?: number): GroupedReaction[] {
  const grouped = new Map<
    string,
    {
      emoji: string
      hasCurrentUserReacted: boolean
      reactors: ReactorInfo[]
    }
  >()

  for (const reaction of reactions) {
    const existing = grouped.get(reaction.emoji)

    const reactorInfo: ReactorInfo = {
      id: reaction.createdBy || reaction.externalCreatedById || reaction.id,
      name: reaction.externalCreatedByName || 'MC User',
      isExternal: !!reaction.externalReactionId,
    }

    const isCurrentUser = reaction.createdBy === currentUserId

    if (existing) {
      existing.reactors.push(reactorInfo)
      if (isCurrentUser) {
        existing.hasCurrentUserReacted = true
      }
    } else {
      grouped.set(reaction.emoji, {
        emoji: reaction.emoji,
        reactors: [reactorInfo],
        hasCurrentUserReacted: isCurrentUser,
      })
    }
  }

  return Array.from(grouped.values()).map((g) => ({
    ...g,
    count: g.reactors.length,
  }))
}

/**
 * Formats reactor names for tooltip display.
 * Shows first 3 names, then "+N more" if there are more.
 */
export function formatReactorNames(reactors: ReactorInfo[]): string {
  const MAX_NAMES = 3
  const names = reactors.slice(0, MAX_NAMES).map((r) => r.name)
  const remaining = reactors.length - MAX_NAMES

  if (remaining > 0) {
    return `${names.join(', ')} and ${remaining} more`
  }
  return names.join(', ')
}
